import { classList } from '@findigs/helpers/src/helpers/class-list';
import { useForwardedRefProxy } from '@findigs/helpers/src/hooks/use-forwarded-ref-proxy';
import type { JSX } from 'react';
import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { cFalse, cTrue, styles } from './form-multiple-choice.module.scss';
import CheckFalseIcon from '../../images/checkbox-false.inline.svg';
import CheckTrueIcon from '../../images/checkbox-true.inline.svg';

export type ChoiceOption = {
  value: string;
  text: string;
  description?: string;
  disabled?: boolean;
  className?: string;
  style?: React.CSSProperties;
};

export type ChoiceOptionCustom = {
  value: string;
  render: () => JSX.Element;
  disabled?: boolean;
  className?: string;
  style?: React.CSSProperties;
};

export type FormMultipleChoiceProps = Omit<
  React.HTMLProps<HTMLDivElement>,
  'as' | 'ref'
> & {
  options: readonly (string | ChoiceOption | ChoiceOptionCustom)[];
  /** @default false */
  single?: boolean;
  hint?: string;
  error?: FormFieldError;
  /** @default false */
  disabled?: boolean;
  /** @default false */
  checkboxes?: boolean;
  /** @default false */
  borderless?: boolean;
};

function isEmpty(parent: HTMLDivElement) {
  return parent.querySelectorAll(':checked').length === 0;
}

/**
 * Internally implemented using `checkbox` and `radio` input elements.
 * `id` and `name` props are forwarded.
 */
export const FormMultipleChoice = React.forwardRef(
  (
    {
      id,
      name,
      options,
      single,
      hint,
      error,
      className,
      disabled = false,
      checkboxes = false,
      borderless = false,
      ...props
    }: FormMultipleChoiceProps,
    ref: React.ForwardedRef<HTMLInputElement>
  ) => {
    const _id = useId();
    const outerRef = useRef<HTMLDivElement>(null);

    const [empty, setEmpty] = useState(() => {
      const v = props.value ?? props.defaultValue;
      return (
        v == null ||
        ((typeof v === 'string' || Array.isArray(v)) && v.length === 0)
      );
    });

    const invalidateEmptyState = useCallback(() => {
      if (outerRef.current) {
        setEmpty(isEmpty(outerRef.current));
      }
    }, []);

    // invalidate empty state when options change
    useEffect(invalidateEmptyState, [options, invalidateEmptyState]);

    // detect low level changes to `.checked` made by RHF
    const { callbackRef } = useForwardedRefProxy(ref, [
      {
        prop: 'checked',
        setter: (v) => {
          // let the DOM/react flush state updates before evaluating
          setTimeout(() => {
            if (outerRef.current) {
              setEmpty(isEmpty(outerRef.current));
            }
          }, 0);
          return v as boolean;
        },
      },
    ]);

    const classesOuter = classList(styles, className);
    const classesInner = classList('choice', {
      empty,
      invalid: error,
      checkboxes,
      borderless,
    });

    const anyOptionHasDescription = useMemo(
      () =>
        options.some(
          (x) => typeof x !== 'string' && 'description' in x && x.description
        ),
      [options]
    );

    return (
      <div className={classesOuter} ref={outerRef} {...props}>
        <div className={classesInner}>
          {options.map((x) => {
            const [
              renderText,
              renderDescription,
              value,
              _disabled,
              className,
              style,
            ] =
              typeof x === 'string'
                ? [() => x, undefined, x, false, '', {}]
                : [
                    'render' in x ? x.render : () => x.text,
                    'description' in x ? () => x.description : undefined,
                    x.value,
                    x.disabled,
                    x.className,
                    x.style,
                  ];

            const defaults = props.defaultValue ?? props.value;
            const defaultChecked = Array.isArray(defaults)
              ? defaults.includes(value)
              : defaults === value;

            const key = `${id ?? _id}-${btoa(value)}`;

            return (
              <div key={key} className="choice-item">
                <input
                  ref={callbackRef}
                  type={single ? 'radio' : 'checkbox'}
                  value={value}
                  name={name ?? _id}
                  id={key}
                  disabled={disabled || _disabled}
                  onChange={invalidateEmptyState}
                  defaultChecked={defaultChecked}
                />
                <label htmlFor={key} className={className} style={style}>
                  {checkboxes && (
                    <>
                      <CheckFalseIcon
                        width={24}
                        height={24}
                        className={cFalse}
                        aria-hidden="true"
                      />
                      <CheckTrueIcon
                        width={24}
                        height={24}
                        className={cTrue}
                        aria-hidden="true"
                      />
                    </>
                  )}
                  {anyOptionHasDescription ? (
                    <>
                      <span className="title">{renderText()}</span>
                      {renderDescription && (
                        <span className="description">
                          {renderDescription()}
                        </span>
                      )}
                    </>
                  ) : (
                    renderText()
                  )}
                </label>
                {!empty && !disabled && !_disabled && error?.message ? (
                  <p className="error" role="alert">
                    {error?.message}
                  </p>
                ) : null}
              </div>
            );
          })}
        </div>
        {!disabled && empty && error?.message ? (
          <p className="error" role="alert">
            {error?.message}
          </p>
        ) : hint ? (
          <>
            <p className="hint" role="alert">
              {hint}
            </p>
          </>
        ) : null}
      </div>
    );
  }
);
FormMultipleChoice.displayName = 'FormMultipleChoice';
